/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import * as yup from 'yup'
import { WF_STEP_TYPE, WF_STEP_PROCESSING_TYPE } from '~/constants/workflow-steps'

/**
 * Validation schema for Remove Document modal form
 * Ported from lego/page/browser/request-removal/FormValidation.jsx
 */

// Set custom locale messages for Yup
yup.setLocale({
  mixed: {
    required: 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
  }
})

// Task team step label schema - can be an object or empty string from API
const taskTeamStepLabel = yup
  .mixed()
  .nullable()
  .transform((value: unknown) => {
    // Transform empty string to null, otherwise keep the value
    if (value === '' || value === undefined) {
      return null
    }
    return value
  })
  .default(null)

// Error message for N_OF_ALL validation
const someAtOnceErrorMessage = 'The # of users cannot be empty or set to 0, it must be set to 1 or more'

// nofAllValue validation - only required when processingType is N_OF_ALL
const nofAllValue = yup.mixed().when('processingType', {
  is: WF_STEP_PROCESSING_TYPE.N_OF_ALL,
  then: () =>
    yup
      .number()
      .min(1, someAtOnceErrorMessage)
      .nullable()
      .transform((value: unknown) => (typeof value === 'number' && isNaN(value) ? undefined : value))
      .required(someAtOnceErrorMessage),
  otherwise: () => yup.mixed().nullable()
})

// Workflow step schema
const workflowStepSchema = yup.object().shape({
  taskTeamStepLabel: taskTeamStepLabel,
  processingType: yup.string().nullable(),
  nofAllValue: nofAllValue,
  merged: yup.array().nullable()
})

/**
 * Create validation schema for Remove Document form
 * @returns Yup validation schema
 */
const createValidationSchema = () => {
  return yup.object().shape({
    description: yup.string().max(254).required().default(''),

    justificationTextId: yup
      .number()
      .min(0, 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR')
      .nullable()
      .when('docType.requireJustification', {
        is: true,
        then: () => yup.number().required()
      }),

    crApprovers: yup.object().shape({
      initialApprovers: yup.object().shape({
        type: yup.string().oneOf(Object.values(WF_STEP_TYPE)).default(WF_STEP_TYPE.NOT_DEFINED),
        steps: yup.array().of(workflowStepSchema).nullable().default([])
      })
    }),

    approvers: yup.object().shape({
      approvers: yup.object().shape({
        type: yup.string().oneOf(Object.values(WF_STEP_TYPE)).default(WF_STEP_TYPE.NOT_DEFINED),
        steps: yup.array().of(workflowStepSchema).nullable().default([])
      })
    })
  })
}

export default createValidationSchema
